import { useState, useEffect, useMemo } from "react";
import classNames from "classnames/bind";
import { appCodeEnum, appStore } from "@/app/store/appStore";
import { MantineThemeOverride } from "@mantine/core";
import defaultStyles from "./themeDefault.module.scss";

export function useTheme() {
	const appCode = appStore((state) => state.appCode);

	const [styles, setStyles] = useState<{ [key: string]: string } | null>(null);

	useEffect(() => {
		// Загрузка стилей в зависимости от appCode
		const loadStyles = async () => {
			let loadedStyles;
			try {
				switch (appCode) {
					case appCodeEnum.aw:
						loadedStyles = (await import("./themeAw.module.scss")).default;
						break;
					case appCodeEnum.ww3:
						loadedStyles = (await import("./themeWW3.module.scss")).default;
						break;
					default:
						loadedStyles = null; // Возвращаем null, если appCode неизвестен
				}
				setStyles(loadedStyles);
			} catch (error) {
				console.error(`Failed to load stylesheet for ${appCode}`, error);
			}
		};

		loadStyles();
	}, [appCode]);

	const theme: MantineThemeOverride = useMemo(() => {
		if (!styles) {
			return {}; // Возвращаем пустую тему, если стили не загружены
		}

		const cx = classNames.bind(styles);

		return {
			components: {
				Button: {
					classNames: {
						root: cx(defaultStyles.customButton, styles.customButton),
						label: cx(styles.customButtonLabel, styles.customButtonLabel)
					}
				},
				Title: {
					classNames: {
						root: cx(defaultStyles.customTitle, styles.customTitle),
					}
				},
				Progress: {
					classNames: {
						root: cx(defaultStyles.customProgressBar, styles.customProgressBar)
					}
				},
				Checkbox: {
					classNames: {
						root: cx(defaultStyles.customCheckbox, styles.customCheckbox),
						input: cx(defaultStyles.customCheckboxInput, styles.customCheckboxInput),
						label: cx(defaultStyles.customCheckboxLabel, styles.customCheckboxLabel)
					}
				},
				Input: {
					classNames: {
						root: cx(defaultStyles.customInputRoot, styles.customInputRoot),
						input: cx(defaultStyles.customInput, styles.customInput),
						label: cx(defaultStyles.customInputLabel, styles.customInputLabel)
					}
				},
				Modal: {
					classNames: {
						header: cx(defaultStyles.customModalHeader),
						content: cx(defaultStyles.customModalContent)
					}
				},
				Loader: {
					classNames: {
						root: cx(defaultStyles.customLoader, styles.customLoader)
					}
				},
				Anchor: {
					classNames: {
						root: cx(defaultStyles.customAnchor, styles.customAnchor)
					}
				},
				Text: {
					classNames: {
						root: cx(defaultStyles.customText, styles.customText)
					}
				},
				ScrollArea: {
					classNames: {
						scrollbar: cx(defaultStyles.customScrollbar, styles.customScrollbar),
						thumb: cx(defaultStyles.customThumb, styles.customThumb),
						corner: cx(defaultStyles.customCorner, styles.customCorner)
					}
				}
			}
		};
	}, [styles]);

	return theme;
}
